(function (host) {
  'use strict';
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const smooth = v => { v = clamp(v, 0, 1); return v * v * (3 - 2 * v); };
  const makeCanvas = (w, h) => {
    const c = host.document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
  };
  function cropRect(width, height, ratio, zoom = 1, fx = .5, fy = .5) {
    zoom = clamp(Number(zoom) || 1, 1, 3);
    ratio = clamp(Number(ratio) || 1.5, .2, 5);
    let w = width, h = w / ratio;
    if (h > height) { h = height; w = h * ratio; }
    w /= zoom; h /= zoom;
    const x = clamp((Number(fx) || 0) * width - w / 2, 0, width - w);
    const y = clamp((Number(fy) || 0) * height - h / 2, 0, height - h);
    return {x, y, width: w, height: h, fx: (x + w / 2) / width, fy: (y + h / 2) / height};
  }
  function boxBlur(input, w, h, radius) {
    const temp = new Float32Array(input.length), out = new Float32Array(input.length);
    const span = radius * 2 + 1;
    for (let y = 0; y < h; y++) {
      let sum = 0;
      for (let x = -radius; x <= radius; x++) sum += input[y * w + clamp(x, 0, w - 1)];
      for (let x = 0; x < w; x++) {
        temp[y * w + x] = sum / span;
        sum += input[y * w + clamp(x + radius + 1, 0, w - 1)] - input[y * w + clamp(x - radius, 0, w - 1)];
      }
    }
    for (let x = 0; x < w; x++) {
      let sum = 0;
      for (let y = -radius; y <= radius; y++) sum += temp[clamp(y, 0, h - 1) * w + x];
      for (let y = 0; y < h; y++) {
        out[y * w + x] = sum / span;
        sum += temp[clamp(y + radius + 1, 0, h - 1) * w + x] - temp[clamp(y - radius, 0, h - 1) * w + x];
      }
    }
    return out;
  }
  function threshold(gray) {
    const hist = new Uint32Array(256);
    let sum = 0;
    for (const v of gray) { const n = Math.round(clamp(v, 0, 255)); hist[n]++; sum += n; }
    let count = 0, subtotal = 0, best = -1, result = 128;
    for (let i = 0; i < 255; i++) {
      count += hist[i]; subtotal += hist[i] * i;
      if (!count || count === gray.length) continue;
      const difference = subtotal / count - (sum - subtotal) / (gray.length - count);
      const variance = count * (gray.length - count) * difference * difference;
      if (variance > best) { best = variance; result = i; }
    }
    return result;
  }
  function process(source, style) {
    const w = source.width, h = source.height, canvas = makeCanvas(w, h);
    const ctx = canvas.getContext('2d', {willReadFrequently: true});
    const sourceData = source.getContext('2d', {willReadFrequently: true}).getImageData(0, 0, w, h);
    const gray = new Float32Array(w * h), data = sourceData.data;
    for (let i = 0; i < gray.length; i++) {
      const p = i * 4, alpha = data[p + 3] / 255;
      gray[i] = (data[p] * .2126 + data[p + 1] * .7152 + data[p + 2] * .0722) * alpha + 255 * (1 - alpha);
    }
    let blur, local, cutoff;
    if (style === 'stencil') { blur = boxBlur(gray, w, h, 2); local = boxBlur(gray, w, h, Math.max(5, Math.round(w / 32))); cutoff = threshold(blur); }
    if (style === 'line') blur = boxBlur(gray, w, h, 1);
    if (style === 'sketch') { blur = boxBlur(gray, w, h, Math.max(3, Math.round(w / 85))); local = boxBlur(gray, w, h, 1); }
    if (style === 'minimal') { blur = boxBlur(gray, w, h, Math.max(2, Math.round(w / 170))); cutoff = threshold(blur); }
    if (style === 'classic' || style === 'cloud') blur = boxBlur(gray, w, h, 1);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = y * w + x, p = i * 4;
      let v = gray[i];
      if (style === 'classic' || style === 'cloud') v = clamp(v + (v - blur[i]) * .55, 0, 255);
      if (style === 'stencil') v = blur[i] < cutoff * .65 + local[i] * .35 - 5 ? 12 : 255;
      if (style === 'line') {
        if (x === 0 || y === 0 || x === w - 1 || y === h - 1) v = 255;
        else {
          const a = blur[i - w - 1], b = blur[i - w], c = blur[i - w + 1];
          const d = blur[i - 1], f = blur[i + 1];
          const g = blur[i + w - 1], j = blur[i + w], k = blur[i + w + 1];
          const gx = -a - 2 * d - g + c + 2 * f + k;
          const gy = -a - 2 * b - c + g + 2 * j + k;
          v = 255 - 240 * smooth((Math.hypot(gx, gy) - 28) / 130);
        }
      }
      if (style === 'sketch') {
        const pencil = Math.min(255, gray[i] * 255 / Math.max(1, blur[i]));
        const tone = (255 - local[i]) / 255;
        const spacing = Math.max(4, Math.round(w / 125));
        const diagonal = (x + y * 2) % spacing < 1.2;
        const cross = ((x - y + w * h) % (spacing + 3)) < 1;
        const hatch = (diagonal && tone > .18) || (cross && tone > .55);
        v = Math.min(pencil, hatch ? 255 - tone * 175 : 255 - tone * 38);
      }
      if (style === 'minimal') v = blur[i] < cutoff * .78 ? 18 : blur[i] < cutoff + 52 ? 151 : 255;
      if (style === 'cloud') {
        const nx = (x + .5 - w / 2) / (w * .5), ny = (y + .5 - h / 2) / (h * .5);
        const angle = Math.atan2(ny, nx);
        const edge = .98 + .045 * Math.sin(angle * 5 + .4) + .028 * Math.sin(angle * 9 - .7);
        const radius = Math.hypot(nx, ny);
        const opacity = smooth((edge - radius) / .27);
        v = 255 - (255 - v) * opacity;
      }
      data[p] = data[p + 1] = data[p + 2] = Math.round(clamp(v, 0, 255)); data[p + 3] = 255;
    }
    ctx.putImageData(sourceData, 0, 0);
    return canvas;
  }
  function board(w, h) {
    const canvas = makeCanvas(w, h), ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ead6b7'; ctx.fillRect(0, 0, w, h);
    for (let y = 0; y < h; y += 3) {
      ctx.beginPath(); ctx.moveTo(0, y);
      for (let x = 0; x <= w; x += 12) ctx.lineTo(x, y + Math.sin(x / 140 + y / 32) * 2.2 + Math.sin(x / 43 + y / 71));
      ctx.strokeStyle = 'rgba(121,84,44,' + (.018 + (Math.sin(y * 1.73) + 1) * .015) + ')';
      ctx.lineWidth = .65; ctx.stroke();
    }
    return canvas;
  }
  function wrapText(ctx, text, width) {
    const result = [];
    for (const paragraph of String(text).split(/\n/)) {
      let line = '';
      for (const word of paragraph.trim().split(/\s+/)) {
        const test = line ? line + ' ' + word : word;
        if (ctx.measureText(test).width <= width) { line = test; continue; }
        if (line) result.push(line);
        line = '';
        for (const char of Array.from(word)) {
          if (line && ctx.measureText(line + char).width > width) { result.push(line); line = ''; }
          line += char;
        }
      }
      if (line) result.push(line);
    }
    return result;
  }
  function drawText(ctx, w, h, state, sample) {
    const text = String(sample || state.text || '').trim().replace(/\s+/g, ' ');
    if (!text) return {lines: [], fontSize: 0, bounds: null};
    const design = state.textDesign || 'elegant';
    const families = {elegant: 'Georgia, serif', handwritten: '"KAD Caveat", cursive', memorial: 'Georgia, serif', modern: 'Arial, sans-serif', romantic: '"KAD Caveat", cursive'};
    let size = Math.min(w * .062 * (Number(state.textSize) || 1), h * .14);
    const maxWidth = w * .85, maxHeight = h * .31;
    let lines;
    while (size >= 1) {
      ctx.font = (design === 'memorial' ? 'italic ' : '') + size.toFixed(2) + 'px ' + families[design];
      lines = wrapText(ctx, text, maxWidth);
      if (lines.length <= 3 && lines.length * size * 1.2 <= maxHeight) break;
      size -= .5;
    }
    const lineHeight = size * 1.2, decor = ['elegant', 'memorial', 'romantic'].includes(design) ? size * .55 : 0;
    const blockHeight = lines.length * lineHeight + decor * 2;
    const margin = h * .065;
    let top = state.textPosition === 'top' ? margin : state.textPosition === 'middle' ? (h - blockHeight) / 2 : h - margin - blockHeight;
    top = clamp(top, margin / 2, h - blockHeight - margin / 2);
    ctx.save();
    const gradient = ctx.createLinearGradient(0, top - size * .4, 0, top + blockHeight + size * .4);
    gradient.addColorStop(0, 'rgba(255,255,255,0)'); gradient.addColorStop(.22, 'rgba(255,255,255,.82)');
    gradient.addColorStop(.78, 'rgba(255,255,255,.82)'); gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient; ctx.fillRect(0, top - size * .4, w, blockHeight + size * .8);
    ctx.fillStyle = '#211b14'; ctx.strokeStyle = '#443421'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.lineWidth = Math.max(1, w / 1100);
    if (design === 'elegant' || design === 'memorial') {
      const length = design === 'memorial' ? w * .16 : w * .34;
      for (const y of [top + decor * .3, top + blockHeight - decor * .3]) { ctx.beginPath(); ctx.moveTo(w / 2 - length / 2, y); ctx.lineTo(w / 2 + length / 2, y); ctx.stroke(); }
    }
    if (design === 'romantic') {
      const y = top + blockHeight - decor * .35;
      ctx.beginPath(); ctx.moveTo(w * .36, y); ctx.quadraticCurveTo(w * .43, y + size * .1, w * .47, y); ctx.moveTo(w * .53, y); ctx.quadraticCurveTo(w * .57, y + size * .1, w * .64, y); ctx.stroke();
      const x = w / 2, s = size * .24;
      ctx.beginPath(); ctx.moveTo(x, y + s * .55); ctx.bezierCurveTo(x - s * 2, y - s * .6, x - s * .6, y - s * 1.7, x, y - s * .6); ctx.bezierCurveTo(x + s * .6, y - s * 1.7, x + s * 2, y - s * .6, x, y + s * .55); ctx.stroke();
    }
    lines.forEach((line, i) => ctx.fillText(line, w / 2, top + decor + lineHeight * (i + .5), maxWidth));
    ctx.restore();
    return {lines, fontSize: size, font: ctx.font, bounds: {x: w * .075, y: top, width: maxWidth, height: blockHeight}};
  }
  function compose(canvas, image, state, wood) {
    const w = canvas.width, h = canvas.height, ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h); ctx.drawImage(wood || board(w, h), 0, 0, w, h);
    ctx.globalCompositeOperation = 'multiply'; ctx.drawImage(image, 0, 0, w, h); ctx.globalCompositeOperation = 'source-over';
    return drawText(ctx, w, h, state);
  }
  const api = {cropRect, process, board, drawText, compose, wrapText};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else host.KozartPersonalizerImage = api;
})(typeof window === 'undefined' ? globalThis : window);
